import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

interface AutoRefreshCountdownProps {
  intervalSeconds: number;
  style?: React.CSSProperties;
  /**
   * Refetches the data in place. When given, the countdown calls it and starts over
   * instead of reloading the page — the tablet keeps its line, week, scroll position
   * and any open modal. Without it the component falls back to a hard page reload.
   */
  onRefresh?: () => void | Promise<void>;
}

/**
 * Shows a live countdown with a progress bar and refreshes the data when it reaches
 * zero. The bar fills up as the next refresh approaches, so it is always visible that
 * auto-refresh is active.
 */
export default function AutoRefreshCountdown({ intervalSeconds, style, onRefresh }: AutoRefreshCountdownProps) {
  const { t } = useLanguage();
  const [secondsLeft, setSecondsLeft] = useState(intervalSeconds);
  // Kept in a ref so a parent that re-creates the callback on every render does not
  // restart the countdown.
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    const timer = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const softRefresh = useCallback(async () => {
    setSecondsLeft(intervalSeconds);
    try {
      await onRefreshRef.current?.();
    } catch {
      // A failed background refresh must not break the countdown — retry on the next tick.
    }
  }, [intervalSeconds]);

  useEffect(() => {
    if (secondsLeft > 0) return;
    if (onRefreshRef.current) {
      softRefresh();
    } else {
      // No in-place refresh available — a hard reload guarantees fresh data.
      window.location.reload();
    }
  }, [secondsLeft, softRefresh]);

  const elapsed = intervalSeconds - Math.max(0, secondsLeft);
  const progress = Math.min(100, Math.max(0, (elapsed / intervalSeconds) * 100));

  // Long intervals (10 min) read better as m:ss than as a raw seconds count.
  const remaining = Math.max(0, secondsLeft);
  const timeLabel = `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`;

  return (
    <div
      title={onRefresh ? t('common.autoRefreshTooltipInPlace') : t('common.autoRefreshTooltip')}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 150,
        ...style,
      }}
    >
      <span
        style={{
          fontSize: 12,
          color: '#64748b',
          whiteSpace: 'nowrap',
          fontWeight: 500,
          ...(style?.color ? { color: style.color } : {}),
        }}
      >
        &#10227; {t('common.refreshInTime', { time: timeLabel })}
      </span>
      <div
        style={{
          width: '100%',
          height: 5,
          borderRadius: 3,
          backgroundColor: 'rgba(148, 163, 184, 0.25)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: '100%',
            backgroundColor: '#667eea',
            borderRadius: 3,
            transition: 'width 1s linear',
          }}
        />
      </div>
    </div>
  );
}
